import type { TFunction } from 'i18next';
import type { useFormInput } from '@/hooks/useFormInput';
import type { PaginatedListResult } from '@/hooks/usePaginatedList';
import type { WeeklyCallSchedule } from '@/types/api';

type FormField = ReturnType<typeof useFormInput<string>>;

export type CallRulesFields = {
  allow_calls?: boolean;
  allow_messaging?: boolean;
  allow_video?: boolean;
  weekly_schedule?: WeeklyCallSchedule;
};

export type AccountDetailToggleField =
  | 'active'
  | 'video'
  | 'calling'
  | 'messaging'
  | 'voicemail'
  | 'window';

export type AccountDetailToggleForm = CallRulesFields & {
  is_active?: boolean;
  voicemail_enabled?: boolean;
};

export type AccountDetailFormField = keyof AccountDetailToggleForm;

const CONTACT_TYPE_KEYS: Record<string, string> = {
  INTERNAL: 'contact.internal',
  EXTERNAL: 'contact.external',
  OTHER: 'contact.other',
  EMERGENCY: 'contact.emergencyService',
  FRIEND: 'contact.friend',
  FAMILY: 'contact.family',
  RELATIVE: 'contact.relative',
  SOLICITOR: 'contact.solicitor',
};

export function getContactTypeLabel(t: TFunction, contactType: string): string {
  const key =
    CONTACT_TYPE_KEYS[contactType] ?? `contact.${contactType.toLowerCase()}`;
  return t(key);
}

export function contactFormFieldError(
  field: FormField,
  submitAttempted: boolean
): string | undefined {
  if (!field.touched && !submitAttempted) {
    return undefined;
  }
  return field.error || undefined;
}

export function scrollToFirstInvalidField(): void {
  const firstErrorInput = document.querySelector('.border-red-300');
  if (firstErrorInput) {
    firstErrorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

export type PersonDisplayFields = {
  first_name?: string;
  last_name?: string;
  username?: string;
  friendly_name?: string;
  full_name?: string;
};

type ManagementEntity = PersonDisplayFields & {
  username: string;
};

export function managementDisplayName(entity: PersonDisplayFields): string {
  return (
    entity.friendly_name?.trim() ||
    [entity.first_name, entity.last_name].filter(namePart => namePart).join(' ')
  );
}

export function personDisplayName(
  entity: PersonDisplayFields | null | undefined,
  fallback = ''
): string {
  if (!entity) {
    return fallback;
  }
  const name = managementDisplayName(entity);
  if (name) {
    return name;
  }
  const fullName = entity.full_name?.trim();
  if (fullName) {
    return fullName;
  }
  return entity.username?.trim() || fallback;
}

export function managementNameLabel(entity: ManagementEntity): string {
  const name = managementDisplayName(entity);
  if (name) {
    return `${name} (${entity.username})`;
  }
  return entity.username;
}

export function isAllowCallsEnabled(value?: boolean): boolean {
  return value ?? true;
}

export function isAllowMessagingEnabled(value?: boolean): boolean {
  return value ?? true;
}

export function usesPersonalCallHours(account: {
  is_nominal?: boolean;
}): boolean {
  return account.is_nominal === true;
}

export function canReadAccountNotes(viewer: {
  is_staff?: boolean;
  is_superuser?: boolean;
} | null): boolean {
  return Boolean(viewer?.is_staff || viewer?.is_superuser);
}

export function readBooleanToggleValue<T>(
  row: T,
  field: keyof T & string
): boolean {
  if (field === 'allow_calls') {
    return isAllowCallsEnabled(row[field] as boolean | undefined);
  }
  if (field === 'allow_messaging') {
    return isAllowMessagingEnabled(row[field] as boolean | undefined);
  }
  return Boolean(row[field]);
}

export async function runOptimisticRowUpdate<
  T extends { id: string | number },
>({
  rowId,
  data,
  setData,
  setUpdating,
  refetchSilent,
  applyPatch,
  revertPatch,
  update,
  onError = 'revert',
}: {
  rowId: string | number;
  data: PaginatedListResult<T> | null;
  setData: (data: PaginatedListResult<T>) => void;
  setUpdating: (id: string | null) => void;
  refetchSilent: () => Promise<void>;
  applyPatch: (row: T) => T;
  revertPatch: (row: T) => T;
  update: () => Promise<unknown>;
  onError?: 'revert' | 'rethrow';
}) {
  try {
    setUpdating(String(rowId));
    if (data) {
      setData({
        ...data,
        results: data.results.map(row =>
          row.id === rowId ? applyPatch(row) : row
        ),
      });
    }
    await update();
    await refetchSilent();
  } catch (e: unknown) {
    if (onError === 'rethrow') {
      await refetchSilent();
      throw e;
    }
    if (data) {
      setData({
        ...data,
        results: data.results.map(row =>
          row.id === rowId ? revertPatch(row) : row
        ),
      });
    }
  } finally {
    setUpdating(null);
  }
}

type OptimisticListState<T extends { id: string | number }> = {
  data: PaginatedListResult<T> | null;
  setData: (data: PaginatedListResult<T>) => void;
  refetchSilent: () => Promise<void>;
  setUpdating: (id: string | null) => void;
};

export function makeOptimisticBooleanToggle<T extends { id: string | number }>(
  list: OptimisticListState<T>,
  field: keyof T & string,
  updateRow: (row: T, nextValue: boolean) => Promise<unknown>
) {
  return async (row: T) => {
    const currentValue = readBooleanToggleValue(row, field);
    await runOptimisticRowUpdate({
      rowId: row.id,
      data: list.data,
      setData: list.setData,
      setUpdating: list.setUpdating,
      refetchSilent: list.refetchSilent,
      applyPatch: item => ({ ...item, [field]: !currentValue }),
      revertPatch: item => ({ ...item, [field]: row[field] }),
      update: () => updateRow(row, !currentValue),
    });
  };
}
